import {Grid} from '@mui/material';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import React from 'react';
import FormikDropdown from '../../../../../components/inputs/FormikDropdown';
import FormikTextField from '../../../../../components/inputs/FormikTextField';
import Panel from '../../../../../components/Panel';
import {HashAlgorithm} from '../../../../../types/HashAlgorithm';
import {SymmetricAlgorithm} from '../../../../../types/SymmetricAlgorithm';
import {DialogTypes} from 'common/names';
import Dialog from 'components/Dialog';
import {INav} from 'features/layout/redux/helpers/FoldersHelper';
import {useTranslation} from 'react-i18next';
import FormikPassphrase from 'components/inputs/FormikPassphrase';
import {
   ICreateOpenPGPKeyPair,
   createOpenPGPKeyPairSchema,
   CreateOpenPGPKeyPairNames,
} from 'types/diplomat/keys/CreateOpenPGPKeyPair';
import {KeyPairType, KeyPairTypeCreatable} from 'types/diplomat/enums/KeyPairType';
import {useModal} from 'common/hooks/useModal';
import {useDispatch, useSelector} from 'react-redux';
import selectors from 'features/layout/redux/selectors';
import FormikTransferListField from 'components/inputs/FormikTransferListField';
import ExpirationField from 'features/layout/DialogRoutes/dialogs/keys/components/ExpirationField';
import actions from 'redux/keys/actions';
import BitStrengthField from './components/BitStrengthField';
import OverwriteConfirmation from './components/OverwriteConfirmation';
import PollingStatusText from 'features/layout/DialogRoutes/dialogs/keys/components/PollingStatusText';
import {castToSend} from 'common/yup/castToSend';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {useFormStatusContext} from 'common/context/FormStatusContext';
import {LoaderInline} from 'components/Loader';
import {useFieldValue} from 'components/hook-form/useFieldValue';
import {enums} from 'types/enums';

type CreateOpenPGPKeyPairDialogProps = {
   data?: INav;
   onClose: () => void;
};

const Form = ({onClose}: Pick<CreateOpenPGPKeyPairDialogProps, 'onClose'>) => {
   const {t} = useTranslation();
   const [status] = useFormStatusContext();
   const keyType = useFieldValue('keyType');

   return (
      <>
         <Panel>
            <Grid container direction={'column'}>
               <Grid item xs={12} sm={6}>
                  <FormikTextField autoFocus name="keyName" label={t('Key Name')} />
               </Grid>
               <Grid item xs={5} sm={3}>
                  <FormikDropdown
                     name="keyType"
                     label={t('Key Type')}
                     options={KeyPairTypeCreatable.options}
                  />
               </Grid>
               {keyType === KeyPairType.enum.ECDSA || keyType === KeyPairType.enum.ECDH ? (
                  <FormikDropdown
                     name={CreateOpenPGPKeyPairNames.curveType}
                     label={t('Elliptic Curve')}
                     options={enums.eccurveType.options}
                  />
               ) : (
                  keyType !== KeyPairType.enum.EDDSA && <BitStrengthField />
               )}
               <Grid item xs={5} sm={3}></Grid>
               <Grid item xs={12} sm={6}>
                  <FormikTextField name="userID" label={t('User ID(s)')} />
               </Grid>
               <Grid item xs={12} sm={6}>
                  <FormikPassphrase name="passPhrase" label={t('Passphrase')} />
               </Grid>
               <Grid item xs={12} sm={6}>
                  <FormikPassphrase name="existingPassphrase" label={t('Repeat Passphrase')} />
               </Grid>
               <Grid item xs={12} sm={6}>
                  <ExpirationField name={'lifetime'} />
               </Grid>
               <Grid item xs={12}>
                  <FormikTransferListField
                     name="symmetricAlgorithms"
                     label={t('Symmetric Algorithm(s):')}
                     leftLabel={t('Available Algorithms')}
                     rightLabel={t('Current Algorithms')}
                     options={SymmetricAlgorithm.options}
                  />
               </Grid>
               <Grid item xs={12}>
                  <FormikTransferListField
                     name="hashAlgorithms"
                     label={t('Hash Algorithm(s):')}
                     leftLabel={t('Available Algorithms')}
                     rightLabel={t('Current Algorithms')}
                     options={HashAlgorithm.options}
                  />
               </Grid>
               <Grid item xs={12}>
                  <PollingStatusText pollCount={status?.pollCount} />
               </Grid>
            </Grid>
         </Panel>
         <FormFooterButtons
            enableOk={!status?.pollCount}
            submitLabel={status?.pollCount ? <LoaderInline color="primary" /> : undefined}
            cancel={onClose}
         />
      </>
   );
};

const CreateOpenPGPKeyPairDialog = ({data, onClose}: CreateOpenPGPKeyPairDialogProps) => {
   const {t} = useTranslation();
   const dispatch = useDispatch();
   const keyPairs = useSelector(selectors.keysOpenPgpSecretKeysSelector);
   const {open, handleOpen, handleClose} = useModal();

   const [submitProps, setSubmitProps] = React.useState<
      [ICreateOpenPGPKeyPair, FormOnSubmitHelpers<ICreateOpenPGPKeyPair>]
   >();
   const handleSubmit = (
      values: ICreateOpenPGPKeyPair,
      helpers: FormOnSubmitHelpers<ICreateOpenPGPKeyPair>
   ) => {
      if (!values.overwrite && keyPairs.some((key) => key.name === values.keyName)) {
         setSubmitProps([values, helpers]);
         handleOpen();
         return;
      }
      dispatch(actions.createKeyPair(castToSend(createOpenPGPKeyPairSchema, values), helpers));
   };

   return (
      <Dialog maxWidth="md" header={DialogTypes.CreateOpenPGPKeyPair.dialogName} close={onClose}>
         <Formik
            initialStatus={{pollCount: 0}}
            initialValues={createOpenPGPKeyPairSchema.cast({folderId: data?.id})}
            validationSchema={createOpenPGPKeyPairSchema}
            onSubmit={handleSubmit}
         >
            <Form onClose={onClose} />
         </Formik>
         <OverwriteConfirmation
            open={open}
            includeExport
            keyType={t('a key pair')}
            keyName={submitProps?.[0].keyName}
            onOk={() => {
               handleSubmit({...submitProps![0], overwrite: true}, submitProps![1]);
               handleClose();
            }}
            onCancel={() => {
               // formikProps.setSubmitting(false);
               handleClose();
            }}
         />
      </Dialog>
   );
};

export default CreateOpenPGPKeyPairDialog;
